/**
 * Every user-facing string for the portal's Integrations surface, in one
 * module — the page itself AND the `CrmGuard` that stands in front of it.
 *
 * The guard is why this isn't simply a const inside `IntegrationsPage.tsx`:
 * it renders INSTEAD of the page when the capability probe failed, and
 * importing the page module into the guard to reach one string would drag the
 * whole api tree — `dataProvider`'s `import.meta` included — behind every test
 * that renders the guard. Same convention as `packages/screens`' `CRM_COPY`.
 *
 * `notEnabledMessage` is the OAuth callback's `not_enabled` answer: a code the
 * PAGE now handles itself, since a provider being switched off no longer
 * hides the page.
 */

/**
 * The `?error=` codes the backend's OAuth callback can redirect back with
 * (viq-backend p4 Task 5); an unrecognized code (should not happen with a
 * matching backend) gets `DEFAULT_ERROR_MESSAGE` rather than a blank toast.
 *
 * Copy is deliberately provider-neutral ("your CRM", not a hardcoded name) —
 * the callback doesn't tell us which provider failed, and p4's original text
 * named HubSpot in every string, which went from "the only live provider" to
 * "wrong for a failed Zoho connect" the moment p5 activated that card
 * (controller-ruled fix, p5 Task 6 review). This map is OAuth-specific and
 * irrelevant to the Salesforce drawer, which has its own inline error
 * handling (`handleSfSubmit`).
 *
 * `not_enabled` is the exception and lives in `notEnabledMessage` below: it is
 * the one code that arrives with a `&source=`, so it can name the provider
 * rather than speak about "CRM integrations" as a whole.
 */
export const ERROR_COPY: Record<string, string> = {
  invalid_state: 'That connection link expired. Please try connecting again.',
  provider_denied: 'That connection was cancelled.',
  exchange_failed: 'Couldn’t finish connecting your CRM. Please try again.',
  internal: 'Something went wrong connecting your CRM. Please try again.',
};

/**
 * `?error=not_enabled` from the OAuth callback, which also sends
 * `&source=<provider>` so the message can name it. A callback from a backend
 * that predates per-CRM flags sends no source; fall back to the generic line.
 */
export function notEnabledMessage(label: string | null | undefined): string {
  return label
    ? `${label} isn’t switched on for your account. Contact VendoIQ support.`
    : 'CRM integrations are not enabled for your account. Contact VendoIQ support.';
}

export const DEFAULT_ERROR_MESSAGE = 'Something went wrong. Please try again.';

/** What `CrmGuard` renders when the entitlement probe itself failed. */
export const GUARD_COPY = {
  unknownTitle: 'Couldn’t check your CRM access',
  unknownBody:
    'We couldn’t confirm which CRM integrations are switched on for your account. Nothing has changed — try again.',
  retryButton: 'Retry',
} as const;

/**
 * A provider card whose flag is OFF for this company. The card stays on the
 * page — greyed, named, and honest about why — because seeing what VendoIQ
 * can connect is the point of the page. Its only live control is Disconnect,
 * and only when a credential is still stored: the flag blocks USE of a CRM,
 * not the tenant's right to revoke what it already gave us (the backend
 * leaves the connections list + delete ungated for exactly that).
 */
export const NOT_ENABLED_COPY = {
  tag: 'Not enabled',
  body: (label: string) =>
    `${label} isn’t switched on for your account. Contact VendoIQ support to have it switched on.`,
  /**
   * Shown on the card, not only in the confirm dialog: an admin who first
   * reads the one-way consequence inside the confirm has already decided by
   * then. "On file" rather than "stored": a staff-created row may hold no
   * credential yet, and it is still theirs to remove.
   */
  stillStored: (label: string) =>
    `A ${label} connection is still on file — you can remove it here. You won’t be able to connect ${label} again until VendoIQ switches it back on.`,
  /** Body of the Disconnect confirm on a not-enabled card. */
  disconnectConfirm: (label: string) =>
    `This deletes the ${label} connection on file. You won’t be able to connect ${label} again until VendoIQ switches it back on for your account.`,
} as const;
